import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { memberService } from '../../services/memberService';
import toast from 'react-hot-toast';

const MemberApproval = () => {
  const queryClient = useQueryClient();
  const [selectedMember, setSelectedMember] = useState(null);

  const { data, isLoading } = useQuery(
    'pending-members',
    () => memberService.getMembers({ status: 'pending_approval' })
  );

  const approveMutation = useMutation(
    (memberId) => memberService.approveMember(memberId),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('pending-members');
        toast.success('Member approved successfully');
        setSelectedMember(null);
      },
      onError: (error) => {
        toast.error(error.response?.data?.detail || 'Approval failed');
      }
    }
  );

  const verifyNidMutation = useMutation(
    ({ memberId, nid }) => memberService.verifyNid(memberId, nid),
    {
      onSuccess: () => {
        toast.success('NID verified successfully');
        queryClient.invalidateQueries('pending-members');
      }
    }
  );

  const handleApprove = (member) => {
    if (member.nid_verified) {
      approveMutation.mutate(member.id);
    } else {
      toast.error('Please verify NID first');
    }
  };

  if (isLoading) return <div>Loading...</div>;

  const pendingMembers = data?.data || [];

  return (
    <div className="p-6">
      <h1 className="text-2xl font-bold mb-6">Member Approval Queue</h1>

      {pendingMembers.length === 0 ? (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-green-700">
          No pending members for approval
        </div>
      ) : (
        <div className="space-y-4">
          {pendingMembers.map((member) => (
            <div key={member.id} className="bg-white rounded-lg shadow p-6">
              <div className="flex justify-between items-start">
                <div>
                  <h3 className="text-lg font-semibold">{member.name}</h3>
                  <p className="text-gray-600">Code: {member.member_code}</p>
                  <p className="text-gray-600">Phone: {member.phone}</p>
                  <p className="text-gray-600">Joined: {new Date(member.joining_date).toLocaleDateString()}</p>

                  <div className="mt-2">
                    <span className={`px-2 py-1 text-xs rounded-full ${
                      member.nid_verified ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                    }`}>
                      NID: {member.nid_verified ? 'Verified' : 'Pending Verification'}
                    </span>
                  </div>
                </div>

                <div className="space-x-2">
                  {!member.nid_verified && (
                    <button
                      onClick={() => {
                        const nid = prompt('Enter NID number:');
                        if (nid) {
                          verifyNidMutation.mutate({ memberId: member.id, nid });
                        }
                      }}
                      className="px-4 py-2 bg-yellow-500 text-white rounded hover:bg-yellow-600"
                    >
                      Verify NID
                    </button>
                  )}

                  <button
                    onClick={() => handleApprove(member)}
                    disabled={approveMutation.isLoading}
                    className="px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600 disabled:opacity-50"
                  >
                    {approveMutation.isLoading ? 'Approving...' : 'Approve Member'}
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default MemberApproval;